import { formatBytes, matchesFileType } from '../../shared-ui-layer/types/validation.types'

import {
  getCDNMapping,
  extractCDNFilePath,
  getSourceFromBucket,
  isGCSUrl,
  extractBucketFromUrl,
  extractFilePath,
  isCDNOrGCSUrl,
} from './url.utils'
import type { ValidationError } from '../../shared-ui-layer/types/validation.types'
import type { SourceInfo, VideoValidation } from '../types'

export type { VideoValidation } from '../types'

/**
 * Narrows the broad source type to video-compatible SourceInfo sources.
 * SourceInfo.appSource only accepts 'communities' | 'courses',
 * so 'client-portal' and 'gokollab' are mapped to 'communities'.
 *
 * Note: Video SourceInfo does not support events-management app source.
 *
 * @param source - Raw source identifier from bucket detection
 * @returns Narrowed source compatible with SourceInfo, or null
 *
 * @example
 * narrowSourceForVideo('courses')
 * // Returns: 'courses'
 *
 * @example
 * narrowSourceForVideo('client-portal')
 * // Returns: 'communities'
 *
 * @example
 * narrowSourceForVideo('gokollab')
 * // Returns: 'communities'
 */
function narrowSourceForVideo(source: string | null): 'communities' | 'courses' | null {
  if (source === 'client-portal' || source === 'gokollab') return 'communities'
  return source as 'communities' | 'courses' | null
}

/**
 * Extracts SourceInfo from a video URL
 *
 * @param url - Video URL (GCS URL, CDN URL, or path)
 * @param defaultSource - Default app source if can't detect (default: 'communities')
 * @returns SourceInfo object or null if URL is not a valid GCS/CDN URL
 *
 * @example
 * // With GCS URL
 * const sourceInfo = extractSourceInfoFromUrl(
 *   'https://storage.googleapis.com/revex-membership-production/videos/lesson.mp4'
 * )
 * // Returns: { appSource: 'courses', bucket: 'revex-membership-production', filePath: 'videos/lesson.mp4' }
 *
 * @example
 * // With CDN URL
 * const sourceInfo = extractSourceInfoFromUrl(
 *   'https://cdn.courses.apisystem.tech/memberships/path/video.mp4'
 * )
 * // Returns: { appSource: 'courses', bucket: 'revex-membership-production', filePath: 'memberships/path/video.mp4' }
 *
 * @example
 * // With invalid URL
 * const sourceInfo = extractSourceInfoFromUrl('not-a-gcs-url')
 * // Returns: null
 */
export function extractSourceInfoFromUrl(
  url: string | undefined,
  defaultSource: 'communities' | 'courses' = 'communities'
): SourceInfo | null {
  if (!url) return null

  // Check if URL is from a known CDN first
  const cdnMapping = getCDNMapping(url)

  if (cdnMapping) {
    // CDN URL detected - use mapped bucket
    const bucket = cdnMapping.bucket

    // Extract file path from CDN URL (entire pathname without leading slash)
    const filePath = extractCDNFilePath(url)

    // Try to auto-detect source from bucket name
    const detectedSource = narrowSourceForVideo(getSourceFromBucket(bucket))
    const appSource: 'communities' | 'courses' = detectedSource || defaultSource

    return {
      appSource,
      bucket,
      filePath,
    }
  }

  // Check if it's a valid GCS URL
  if (!isGCSUrl(url)) {
    return null
  }

  // Extract bucket name from URL
  const bucket = extractBucketFromUrl(url)
  if (!bucket) {
    console.warn('⚠️ Cannot extract bucket from URL:', url)
    return null
  }

  // Try to auto-detect source from bucket name
  const detectedSource = narrowSourceForVideo(getSourceFromBucket(bucket))
  const appSource: 'communities' | 'courses' = detectedSource || defaultSource

  // Extract file path
  const filePath = extractFilePath(url)

  return {
    appSource,
    bucket,
    filePath,
  }
}

/**
 * Checks if a video URL requires SourceInfo (i.e., it's a bucket/CDN URL without licenseId).
 * Used to decide whether to call extractSourceInfoFromUrl before passing to the video player.
 *
 * @param url - Video URL
 * @param licenseId - Optional license ID
 * @returns True if URL requires SourceInfo generation
 *
 * @example
 * // GCS URL without licenseId → needs SourceInfo
 * requiresSourceInfo('https://storage.googleapis.com/bucket/video.mp4')
 * // Returns: true
 *
 * @example
 * // Has licenseId → DRM handles it, no SourceInfo needed
 * requiresSourceInfo('https://storage.googleapis.com/bucket/video.mp4', 'license-123')
 * // Returns: false
 *
 * @example
 * // YouTube embed → not a bucket URL, no SourceInfo needed
 * requiresSourceInfo('https://youtube.com/watch?v=abc')
 * // Returns: false
 */
export function requiresSourceInfo(url: string | undefined, licenseId?: string): boolean {
  // If licenseId exists, no need for sourceInfo
  if (licenseId) return false

  // If URL is not a CDN or GCS URL, no sourceInfo needed (will use HTML5 or iframe)
  if (!isCDNOrGCSUrl(url)) return false

  return true
}

/**
 * Get video duration from a file by creating a temporary video element.
 * Client-side only (uses URL.createObjectURL).
 *
 * @param file - Video File object
 * @returns Promise resolving to duration in seconds
 *
 * @example
 * const file = inputElement.files[0]
 * const duration = await getVideoDuration(file)
 * // 125.4 (seconds)
 */
export const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      resolve(video.duration)
      URL.revokeObjectURL(video.src)
    }
    video.onerror = reject
    video.src = URL.createObjectURL(file)
  })
}

/**
 * Validate video files against size, type, and duration rules.
 *
 * NOTE: This function only validates file size, type, and duration.
 * maxFiles validation is handled at the composable level (usePostEditor, CommentEditor, etc.)
 * to allow selecting multiple files and then enforcing the limit when adding to the model.
 *
 * @param files - Array of File objects to validate
 * @param validation - Validation rules (VideoValidation)
 * @returns Object with valid files and validation errors
 *
 * @example
 * const { valid, errors } = await validateVideoFiles(
 *   fileList,
 *   { maxFileSize: 500 * 1024 * 1024, acceptedTypes: ['video/mp4', 'video/webm'], maxDuration: 180 }
 * )
 * // valid: File[] — files that passed all checks
 * // errors: ValidationError[] — details for each failed file
 */
export const validateVideoFiles = async (
  files: File[],
  validation: VideoValidation
): Promise<{ valid: File[]; errors: ValidationError[] }> => {
  const errors: ValidationError[] = []
  const validFiles: File[] = []

  for (const file of files) {
    const fileErrors: ValidationError[] = []

    // File size validation
    if (validation.maxFileSize && file.size > validation.maxFileSize) {
      fileErrors.push({
        field: 'fileSize',
        message: `Video "${file.name}" exceeds ${formatBytes(validation.maxFileSize)}`,
        type: 'error',
        meta: { fileName: file.name, size: file.size, maxSize: validation.maxFileSize },
      })
    }

    if (validation.minFileSize && file.size < validation.minFileSize) {
      fileErrors.push({
        field: 'fileSize',
        message: `Video "${file.name}" is smaller than ${formatBytes(validation.minFileSize)}`,
        type: 'error',
        meta: { fileName: file.name, size: file.size, minSize: validation.minFileSize },
      })
    }

    // MIME type validation
    if (validation.acceptedTypes && validation.acceptedTypes.length > 0) {
      const isAccepted = validation.acceptedTypes.some(acceptedType => matchesFileType(file.type, acceptedType))
      if (!isAccepted) {
        fileErrors.push({
          field: 'fileType',
          message: `File type "${file.type}" not accepted for "${file.name}"`,
          type: 'error',
          meta: { fileName: file.name, fileType: file.type, acceptedTypes: validation.acceptedTypes },
        })
      }
    }

    // Duration validation
    if (validation.maxDuration || validation.minDuration) {
      try {
        const duration = await getVideoDuration(file)

        // Broken or stub codecs can yield Infinity/NaN — do not block upload on unreadable duration
        if (Number.isFinite(duration)) {
          if (validation.maxDuration && duration > validation.maxDuration) {
            fileErrors.push({
              field: 'duration',
              message: `Video "${file.name}" exceeds maximum duration (${validation.maxDuration}s)`,
              type: 'error',
              meta: { fileName: file.name, duration, maxDuration: validation.maxDuration },
            })
          }

          if (validation.minDuration && duration < validation.minDuration) {
            fileErrors.push({
              field: 'duration',
              message: `Video "${file.name}" is shorter than minimum duration (${validation.minDuration}s)`,
              type: 'error',
              meta: { fileName: file.name, duration, minDuration: validation.minDuration },
            })
          }
        }
      } catch (error) {
        console.error('Failed to get video duration:', error)
      }
    }

    if (fileErrors.length === 0) {
      validFiles.push(file)
    } else {
      errors.push(...fileErrors)
    }
  }

  return { valid: validFiles, errors }
}
